import {
  Box,
  Button,
  LoadingIndicator,
  LocationOffline,
  LocationOnline,
  MessageBlock,
  NavList,
} from '@deckee/deck-hand';
import React, { FC, MouseEvent, ReactNode, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { openWithInAppBrowser } from '../../../../../services/in-app-browser';
import { selectUnitsConfig } from '../../../../../services/store/modules/app-controls';
import { Signal } from '../../../../../services/store/modules/signal/models';
import { formatLatLon } from '../../../../helpers/coordinate-helpers';
import HeaderBar from '../../../../layouts/components/HeaderBar/HeaderBar';
import HeaderBarButton from '../../../../layouts/components/HeaderBarButton/HeaderBarButton';
import SheetLayout, {
  SheetBody,
} from '../../../../layouts/SheetLayout/SheetLayout';
import IconWithTextLockup from '../../../../lockups/IconWithTextLockup/IconWithTextLockup';
import { close, link, ReactSvg } from '../../../../shared/vector-images';
import MemberAvatar from '../../../../shared/_deprecated/components/MemberAvatar/MemberAvatar';
import BatteryLevelIndicator from '../BatteryLevelIndicator';
import useRelativeTime from '../../helpers/relative-time';
import UserShareItem from '../UserShareItem/UserShareItem';

export interface GroupDetailMember {
  id: string;
  memberId: string;
  username: string;
  signalId?: string;
  signalImage?: string;
  signalTimestamp?: string;
  signalSubscribed?: boolean;
  distanceFromMember?: string;
  isCurrentUser?: boolean;
  primaryWatercraftName?: string;
}

export interface GroupDetailViewProps {
  isLoading?: boolean;
  error?: boolean;
  groupName?: string;
  groupIconUrl?: string;
  groupIcon?: React.ReactNode;
  memberCountLabel?: string;
  members?: GroupDetailMember[];
  noMembersMessage?: string;
  selectedSignalId?: string;
  /** When a member (watercraft/signal) is selected, show their signal details (lat/lon, battery, icon, data URL) */
  selectedSignal?: Signal | null;
  onBack: () => void;
  onClose: () => void;
  onGroupClick?: () => void;
  onMemberClick: (signalId: string) => void;
}

const GroupPageHeading = styled(Box).attrs({
  fontSize: 0,
  color: 'darkergrey',
  mt: 2,
  mb: 1,
  pl: 1,
  textAlign: 'left',
})`
  text-transform: uppercase;
`;

const GroupDetailView: FC<GroupDetailViewProps> = ({
  isLoading = false,
  error = false,
  groupName,
  groupIconUrl,
  groupIcon,
  memberCountLabel,
  noMembersMessage,
  members = [],
  selectedSignalId,
  selectedSignal,
  onBack,
  onClose,
  onGroupClick,
  onMemberClick,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const unitsConfig = useSelector(selectUnitsConfig);
  const hasError = error || !groupName;

  const formattedLatLon =
    selectedSignal?.latitude != null && selectedSignal?.longitude != null
      ? formatLatLon(
          (unitsConfig?.coordinate as string) ?? 'coordinate',
          selectedSignal.latitude,
          selectedSignal.longitude,
        )
      : null;
  const lastKnownRelativeTime = useRelativeTime(
    selectedSignal?.timestamp ?? '',
  );
  const dataUrl =
    selectedSignal?.data &&
    typeof selectedSignal.data === 'object' &&
    'url' in selectedSignal.data
      ? (selectedSignal.data as { url?: string }).url
      : undefined;

  const getHeading = () => {
    if (isLoading) return 'Loading...';
    if (hasError) return 'Error';
    return 'Live sharing';
  };

  const renderContent = () => {
    if (isLoading) {
      return <LoadingIndicator />;
    }

    if (hasError) {
      return <MessageBlock type="error">Error loading group</MessageBlock>;
    }

    return (
      <>
        <GroupPageHeading>Group</GroupPageHeading>
        <NavList>
          <NavList.Item
            chevron
            p="3"
            border="2px solid"
            borderColor="primary"
            onClick={onGroupClick || onBack}
          >
            <IconWithTextLockup
              image={
                groupIcon || (
                  <MemberAvatar
                    memberAvatarUrl={groupIconUrl}
                    width={50}
                    height={50}
                  />
                )
              }
              heading={groupName}
              description={memberCountLabel}
            />
          </NavList.Item>
        </NavList>

        {members.length > 0 ? (
          <>
            <GroupPageHeading>Members</GroupPageHeading>
            {members.map((member) => {
              const isSelected =
                selectedSignalId != null &&
                member.signalId != null &&
                String(member.signalId) === String(selectedSignalId);
              const showDetails = isSelected && selectedSignal;

              return (
                <UserShareItem
                  key={member.id}
                  padded
                  displayName={
                    member.isCurrentUser
                      ? `${member.username} (You)`
                      : member.username
                  }
                  memberId={member.memberId}
                  userImageId={member.signalImage || ''}
                  timestamp={member.signalTimestamp}
                  subscriber={member.signalSubscribed}
                  distanceFromMember={member.distanceFromMember}
                  selected={isSelected}
                  itemAction={() => onMemberClick(member.signalId || '')}
                  expandedContent={
                    showDetails ? (
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                      >
                        <Box fontSize={1} color="darkergrey">
                          {selectedSignal.on_the_water
                            ? formattedLatLon
                            : 'Not on the water'}
                        </Box>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                          flexWrap="wrap"
                        >
                          {selectedSignal.online ? (
                            <LocationOnline
                              color={theme.colors.primary}
                              size="22px"
                            />
                          ) : (
                            <LocationOffline
                              color={theme.colors.darkergrey}
                              size="22px"
                            />
                          )}
                          <Box fontSize={1} pl="1" color="darkergrey">
                            {selectedSignal.online ? 'Active ' : 'Last seen '}
                            {lastKnownRelativeTime}
                          </Box>
                          {selectedSignal.online && (
                            <Box fontSize={1} color="darkergrey">
                              <BatteryLevelIndicator
                                marginLeft={2}
                                batteryLevel={
                                  selectedSignal.battery_level ?? null
                                }
                              />
                            </Box>
                          )}
                        </Box>
                        {member.primaryWatercraftName && (
                          <Box width="100%" fontSize={1} color="darkergrey">
                            {member.primaryWatercraftName}
                          </Box>
                        )}
                        {dataUrl && (
                          <Button
                            pl="0!important"
                            py="0!important"
                            pr="0!important"
                            variant="ghost"
                            href={dataUrl}
                            onClick={async (e: MouseEvent<HTMLElement>) => {
                              e.preventDefault();
                              try {
                                await openWithInAppBrowser({ url: dataUrl });
                              } catch {
                                window.open(
                                  dataUrl,
                                  '_blank',
                                  'noopener,noreferrer',
                                );
                              }
                            }}
                          >
                            <Box
                              as="span"
                              fontSize={1}
                              display="inline-flex"
                              alignItems="center"
                            >
                              <ReactSvg
                                toDisplay={link()}
                                options={{ fillColor: theme.colors.primary }}
                                width={16}
                                height={16}
                              />
                              &nbsp;
                              {dataUrl.length > 30
                                ? `${dataUrl.slice(0, 30)}...`
                                : dataUrl}
                            </Box>
                          </Button>
                        )}
                      </Box>
                    ) : undefined
                  }
                />
              );
            })}
          </>
        ) : (
          <Box p={3} textAlign="center" color="darkergrey">
            {noMembersMessage || 'No members in this group'}
          </Box>
        )}
      </>
    );
  };

  return (
    <SheetLayout
      showMenuBar={false}
      heading={getHeading()}
      slideUpTransition
      renderHeaderBar={(heading: ReactNode) => (
        <HeaderBar
          centerNode={heading}
          rightNode={
            <HeaderBarButton
              ariaLabel="close"
              icon={close()}
              onClick={onClose}
            />
          }
        />
      )}
    >
      <SheetBody maxHeight="370px" overflowY="auto" ref={ref} pt="0!important">
        {renderContent()}
      </SheetBody>
    </SheetLayout>
  );
};

export default GroupDetailView;
